/**
 * @fileoverview A user's own name: the one rule set (US #1814, Task #1820 A26; Task #2415)
 *
 * Used by BOTH the BFF (`GET` / `PATCH /api/profile`, `server/routes/profile.routes.ts` and
 * `server/repositories/users.repository.ts`) and the profile dialog
 * (`components/UserProfileDialog.vue`), so the message shown inline before a request is the
 * message the server would have answered with. Pure, no imports: the server build
 * (`tsconfig.server.json`, CommonJS) reaches it by a relative path.
 *
 * Task #2415: the profile takes a first and a last name. Each is checked with the same rules as
 * the single display name, and the two are joined into `users.display_name` ("<first> <last>"),
 * which every existing reader (users list, header, panel, audit) keeps reading unchanged.
 *
 * @module utils/display-name.utils
 */

/** The longest display name we store, counted in characters after trimming and collapsing. */
export const DISPLAY_NAME_MAX_LENGTH = 100

/**
 * The longest first or last name. Chosen so the joined display name ("<first> <last>") can never
 * exceed {@link DISPLAY_NAME_MAX_LENGTH}: 49 + 1 space + 49 = 99.
 */
export const NAME_PART_MAX_LENGTH = Math.floor((DISPLAY_NAME_MAX_LENGTH - 1) / 2)

export type DisplayNameResult = { ok: true; value: string } | { ok: false; error: string }

export type NameField = 'firstName' | 'lastName'

/** A validated first and last name, and the display name they join into. */
export interface PersonName {
  firstName: string
  lastName: string
  /** `"<firstName> <lastName>"`: what `users.display_name` stores. */
  displayName: string
}

/**
 * `error` is the first failing field's message (first name before last name), for callers that
 * show one message; `fieldErrors` has a message for every field that failed.
 */
export type PersonNameResult =
  | { ok: true; value: PersonName }
  | { ok: false; error: string; fieldErrors: Partial<Record<NameField, string>> }

/**
 * C0 controls (including tab, line feed and carriage return), DEL, C1 controls, and the two
 * Unicode line-breaking separators U+2028/U+2029: the same set `server/utils/validators.ts`'s
 * `stripControlCharacters` removes. A display name REJECTS them rather than stripping them: the
 * user is typing into a field and can see the message, and a name is shown in lists, emails and
 * the audit trail, where a hidden line break would be spoofing, not formatting.
 */
// eslint-disable-next-line no-control-regex -- deliberately matches control characters; see above
const CONTROL_CHARACTERS_RE = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]/

/** Trim, and collapse every run of whitespace (including Unicode spaces) to one plain space. */
function collapseWhitespace(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

/** The shared rule: `label` names the field in the message, `max` is its character limit. */
function normaliseName(raw: unknown, label: string, max: number): DisplayNameResult {
  if (typeof raw !== 'string') return { ok: false, error: `${label} is required` }
  if (CONTROL_CHARACTERS_RE.test(raw)) {
    return {
      ok: false,
      error: `${label} must not contain tabs, line breaks or other control characters`,
    }
  }
  const value = collapseWhitespace(raw)
  if (value.length === 0) return { ok: false, error: `${label} must not be empty` }
  // Array.from counts code points, so an emoji or a non-BMP letter is one character.
  if (Array.from(value).length > max) {
    return { ok: false, error: `${label} must be at most ${max} characters` }
  }
  return { ok: true, value }
}

/**
 * Validate and normalise a display name: trim it, collapse every run of whitespace (including
 * non-breaking and other Unicode spaces) to one plain space, then require 1 to
 * {@link DISPLAY_NAME_MAX_LENGTH} characters with no control characters.
 */
export function normaliseDisplayName(raw: unknown): DisplayNameResult {
  return normaliseName(raw, 'Name', DISPLAY_NAME_MAX_LENGTH)
}

/**
 * Validate and normalise a first and last name (Task #2415). Both are required, and each gets the
 * display-name rules with a {@link NAME_PART_MAX_LENGTH} limit. On success, `displayName` is the
 * two joined by one space, which always satisfies {@link normaliseDisplayName}.
 */
export function normalisePersonName(firstName: unknown, lastName: unknown): PersonNameResult {
  const first = normaliseName(firstName, 'First name', NAME_PART_MAX_LENGTH)
  const last = normaliseName(lastName, 'Last name', NAME_PART_MAX_LENGTH)
  if (!first.ok || !last.ok) {
    const fieldErrors: Partial<Record<NameField, string>> = {}
    if (!first.ok) fieldErrors.firstName = first.error
    if (!last.ok) fieldErrors.lastName = last.error
    const error = !first.ok ? first.error : !last.ok ? last.error : ''
    return { ok: false, error, fieldErrors }
  }
  const joined = normaliseDisplayName(`${first.value} ${last.value}`)
  // Unreachable while NAME_PART_MAX_LENGTH is derived from DISPLAY_NAME_MAX_LENGTH; kept so a
  // future change to either limit cannot store a display name its own rule would refuse.
  if (!joined.ok) return { ok: false, error: joined.error, fieldErrors: {} }
  return {
    ok: true,
    value: { firstName: first.value, lastName: last.value, displayName: joined.value },
  }
}

/**
 * Split a single display name into a first and last name, for a user who saved a name before
 * the profile had the two fields (Task #2415): the first word is the first name, the rest is the
 * last name (empty for a one-word name). `null` when there is no name to split.
 */
export function splitDisplayName(
  displayName: string | null
): { firstName: string; lastName: string } | null {
  if (displayName === null) return null
  const value = collapseWhitespace(displayName)
  if (value.length === 0) return null
  const space = value.indexOf(' ')
  return space === -1
    ? { firstName: value, lastName: '' }
    : { firstName: value.slice(0, space), lastName: value.slice(space + 1) }
}
